import { Group, SaveButton } from '@/components/_common';
import { Badge, Button, Form, Modal, ModalProps, message } from 'antd';
import StatusTag from '@/components/_common/StatusTag';
import { InvoiceCreateDTO } from '@/core/domain/invoice/invoice/Invoice.types';
import TabsCustom from '@/components/_common/TabsCustom';
import { useCallback, useEffect, useState } from 'react';
import {
  useCreateInvoiceNoteItems,
  useUpdateInvoiceNoteItemsId,
} from '@/core/domain/invoice/invoiceNoteItems/invoiceNoteItemsParts/InvoiceNoteItms.hooks';
import {
  InvoiceItemCreateRequest,
  InvoiceItemTaxes,
  TypeEnum,
} from '@/core/domain/invoice/invoiceNoteItems/invoiceNoteItemsParts/InvoiceNoteItems.types';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';
import { useCalcTaxesItems } from '@/core/domain/invoice/invoiceItem/invoiceItem.hook';
import { Task } from '@/core/domain/operational/Tasks/Tasks.types';
import InvoiceServiceDedutionForm from './DedutionForm';
import InvoiceServiceGeneralDataForm from './GeneralDataForm';
import InvoiceServiceIbtpForm from './IbtpForm';
import InvoiceServiceIssForm from './IssForm';
import {
  useInvoiceServiceFillFields,
  useInvoiceServiceDetailFillFieds,
} from './InvoiceService.hooks';
import { InvoiceServiceFormValues } from './InvoiceServiceForm.types';
import InvoiceServiceRetentionForm from './RetentionForm';
import { useInvoiceFormContext } from '../../InvoiceFormProvider';
import IbsCbsFormFields from '@/components/_common/TaxReform/IbsCbsFormFields';

interface Props extends ModalProps {
  task?: Task;
  invoice?: InvoiceCreateDTO; // TODO: Corrigir a tipagem
  onCancel: VoidFunction;
  invoiceItemId?: number;
}

const InvoiceItemServiceModal: React.FC<Props> = (props) => {
  const { task, invoice, invoiceItemId, ...others } = props;

  const { invoiceSettingNfs, disabledFields } = useInvoiceFormContext();

  const [form] = Form.useForm<InvoiceServiceFormValues>();
  const formValues = Form.useWatch([], form);

  const [menuKey, setMenuKey] = useState('general');
  const [tabErrors, setTabErrors] = useState<string[]>([]);
  const [submitLoading, setSubmitLoading] = useState(false);

  const invoiceItemCreate = useCreateInvoiceNoteItems();
  const invoiceItemEdit = useUpdateInvoiceNoteItemsId();
  const invoiceCalculateTaxesItem = useCalcTaxesItems();

  const [createFillFields, createFieldsLoading] = useInvoiceServiceFillFields({
    companyId: invoice?.companyId,
  });

  const [detailFillFields, detailFieldsLoading] =
    useInvoiceServiceDetailFillFieds();

  const hasLoading =
    submitLoading ||
    createFieldsLoading ||
    detailFieldsLoading ||
    invoiceCalculateTaxesItem.isLoading;

  useEffect(() => {
    // Change grossServiceValue field on values tab
    if (
      typeof formValues?.general?.quantity === 'number' &&
      typeof formValues?.general?.valueUnitary === 'number'
    ) {
      form.setFieldValue(
        ['general', 'grossServiceValue'],
        formValues.general.quantity * formValues.general.valueUnitary,
      );
    }
  }, [formValues?.general?.quantity, formValues?.general?.valueUnitary]);

  useEffect(() => {
    // Change grossServiceValue field on values tab
    if (
      (typeof formValues?.general?.deductions === 'number' ||
        typeof formValues?.general?.unconditionalDiscount === 'number') &&
      typeof formValues?.general?.grossServiceValue === 'number' &&
      !!invoiceSettingNfs?.deductUnconditionalDiscountFromTaxBase
    ) {
      form.setFieldValue(
        ['general', 'taxBase'],
        formValues.general.grossServiceValue -
          (formValues.general.unconditionalDiscount || 0) -
          (formValues.general.deductions || 0),
      );
    }
  }, [
    formValues?.general?.deductions,
    formValues?.general?.unconditionalDiscount,
    formValues?.general?.grossServiceValue,
    invoiceSettingNfs?.deductUnconditionalDiscountFromTaxBase,
  ]);

  useEffect(() => {
    // Automatic completion of fields when creating note item
    (async () => {
      if (invoice && task) {
        const fieldValues = await createFillFields({
          invoiceType: invoice.invoiceType,
          taskId: task?.id,
          customerId: invoice.customerId,
          insurierId: invoice.insurerId,
        });

        if (fieldValues) {
          form.setFieldsValue(fieldValues);
        }
      }
    })();
  }, [invoice, task]);

  useEffect(() => {
    // Filling in fields when editing the note item
    (async () => {
      if (invoiceItemId) {
        const fields = await detailFillFields(invoiceItemId);
        form.setFieldsValue(fields);
      }
    })();
  }, [invoiceItemId]);

  function handleClose() {
    form.resetFields();
    setMenuKey('general');
    setTabErrors([]);
    props.onCancel();
  }

  async function handleSubmit(closeModal = true) {
    try {
      setSubmitLoading(true);

      const values = await form.validateFields();

      const itemTaxes: Partial<InvoiceItemTaxes> = {
        ...values?.itemTaxes?.dedution,
        ...values?.itemTaxes?.ibtp,
        ...values?.itemTaxes?.iss,
        ...values?.itemTaxes?.retention,
        ...values.itemTaxes?.is,
        ...values.itemTaxes?.ibsCbs,
        competenceApuration: values.itemTaxes?.ibsCbs?.competenceApuration
          ? values.itemTaxes?.ibsCbs?.competenceApuration.format()
          : undefined,
        ibsZfmAssessmentPeriod:
          values.itemTaxes?.ibsCbs?.ibsZfmAssessmentPeriod?.format('YYYY-MM'),
        nbsCode: values.itemTaxes?.nbsCode,
        withheld: values.itemTaxes?.iss?.withheld
          ? JSON.parse(values.itemTaxes?.iss?.withheld)
          : undefined,
        federalTaxesWithheld: values.itemTaxes?.federalTaxesWithheld
          ? JSON.parse(values.itemTaxes?.federalTaxesWithheld)
          : undefined,
        withholdingResponsible: values.itemTaxes?.withholdingResponsible,
      };

      const payload: InvoiceItemCreateRequest = {
        ...values.general,
        invoiceId: invoice?.id,
        type: TypeEnum.task,
        itemTaxes: itemTaxes as InvoiceItemTaxes,
      };

      if (invoiceItemId) {
        await invoiceItemEdit.mutateAsync({
          ...payload,
          invoiceId: invoice?.id,
          id: invoiceItemId,
          hiddeNotification: !closeModal,
        });
      } else {
        await invoiceItemCreate.mutateAsync({ ...payload, taskId: task?.id });
      }

      if (closeModal) {
        handleClose();
      }

      return true;
    } catch (err: unknown) {
      message.error('Preencha os campos obrigatórios');

      const error = err as { errorFields?: { name: string[] }[] };

      if (error.errorFields?.length) {
        setTabErrors(
          error.errorFields.reduce((acc, curr) => {
            const name =
              curr.name[0] === 'itemTaxes' ? curr.name[1] : curr.name[0];

            if (!acc.includes(name)) {
              return [...acc, name];
            }

            return acc;
          }, [] as string[]),
        );
      }

      return false;
    } finally {
      setSubmitLoading(false);
    }
  }

  const calculateTaxes = useCallback(async () => {
    try {
      if (invoiceItemId) {
        // Save fields filled
        const result = await handleSubmit(false);

        if (result) {
          // Calcule taxes
          await invoiceCalculateTaxesItem.mutateAsync(invoiceItemId);

          // Update taxes fields
          const fields = await detailFillFields(invoiceItemId);
          form.setFieldsValue(fields);
        }
      }
    } catch {}
  }, [invoiceItemId]);

  const modalTitle = (
    <Group gap={16}>
      <StatusTag label={invoice?.invoiceType} />
      <span>
        {invoiceItemId ? 'Editar serviço da nota' : 'Vincular serviço na nota'}
      </span>
    </Group>
  );

  const modalFooter = (
    <Group position="between" gap={24}>
      <div>
        {!!invoiceItemId && (
          <Button
            type="primary"
            disabled={hasLoading || disabledFields}
            onClick={calculateTaxes}
          >
            Calcular tributos
          </Button>
        )}
      </div>

      <Group gap={24}>
        <Button onClick={handleClose} disabled={hasLoading}>
          Cancelar
        </Button>
        <SaveButton
          onClick={() => handleSubmit()}
          disabled={hasLoading || disabledFields}
        >
          {invoiceItemId ? 'Alterar serviço' : 'Vincular serviço'}
        </SaveButton>
      </Group>
    </Group>
  );

  const tabIconError = (key: string) =>
    tabErrors.includes(key) ? <Badge dot /> : null;

  return (
    <Modal
      {...others}
      onCancel={handleClose}
      title={modalTitle}
      width={1200}
      bodyStyle={{ padding: 0 }}
      maskClosable={false}
      destroyOnClose
      footer={modalFooter}
      closable={!hasLoading}
    >
      <div style={{ marginBottom: 24, position: 'relative' }}>
        {hasLoading && (
          <ModalLoadingOverlay
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              backdropFilter: 'blur(1px)',
            }}
            messages={
              submitLoading
                ? [
                    invoiceItemId
                      ? 'Salvando dados do item...'
                      : 'Vinculando item na nota...',
                    'Atualizando listagem de itens da nota...',
                  ]
                : invoiceCalculateTaxesItem.isLoading
                ? ['Calculando dados de tributos...']
                : ['Preenchendo dados do serviço e tributação...']
            }
          />
        )}

        <TabsCustom key="general" activeKey={menuKey} onChange={setMenuKey}>
          <TabsCustom.Item
            label="Dados gerais"
            key="general"
            icon={tabIconError('general')}
          >
            <InvoiceServiceGeneralDataForm form={form} />
          </TabsCustom.Item>

          <TabsCustom.Item key="iss" label="ISS" icon={tabIconError('iss')}>
            <InvoiceServiceIssForm form={form} />
          </TabsCustom.Item>

          <TabsCustom.Item
            key="retention"
            label="Retenção"
            icon={tabIconError('retention')}
          >
            <InvoiceServiceRetentionForm form={form} />
          </TabsCustom.Item>

          <TabsCustom.Item
            key="dedution"
            label="Dedução"
            icon={tabIconError('dedution')}
          >
            <InvoiceServiceDedutionForm form={form} />
          </TabsCustom.Item>

          {!invoiceSettingNfs?.automaticIbptCalculation && (
            <TabsCustom.Item
              key="ibtp"
              label="IBPT"
              icon={tabIconError('ibtp')}
            >
              <InvoiceServiceIbtpForm form={form} />
            </TabsCustom.Item>
          )}

          {/* <TabsCustom.Item key="is" label="IS" icon={tabIconError('is')}>
            <IsFormFields form={form} disabledFields={disabledFields} />
          </TabsCustom.Item> */}

          <TabsCustom.Item
            key="ibsCbs"
            label="IBS/CBS"
            icon={tabIconError('ibsCbs')}
          >
            <IbsCbsFormFields form={form} disabledFields={disabledFields} />
          </TabsCustom.Item>
        </TabsCustom>
      </div>
    </Modal>
  );
};

export default InvoiceItemServiceModal;
